import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Loader2,
  Plus,
  Car,
  Edit,
  Archive,
  Eye,
  Crown,
  Zap,
  AlertTriangle,
  FileEdit,
  Clock,
  CheckCircle2,
  XCircle,
  PackageOpen,
  Camera,
} from 'lucide-react'
import {
  useMyCarListings,
  useArchiveCarListing,
  usePlatformUsage,
  usePlatformEntitlement,
  usePlatformSubscription,
} from '@/modules/cars/hooks/use-cars'
import { appRoutes } from '@/constants'

export function SellerCarsInventoryPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = searchParams.get('status') || 'ALL'

  const statusParam = activeTab === 'ALL' ? undefined : activeTab
  const { data: listings, isLoading: loadingListings } = useMyCarListings(statusParam)
  const { data: usage, isLoading: loadingUsage } = usePlatformUsage()
  const { data: entitlement } = usePlatformEntitlement()
  const { data: subData } = usePlatformSubscription()
  const archiveListing = useArchiveCarListing()

  const allListings = listings || []

  const stats = {
    total: allListings.length,
    published: allListings.filter((c) => c.status === 'PUBLISHED').length,
    pending: allListings.filter((c) => c.status === 'PENDING_REVIEW').length,
    drafts: allListings.filter((c) => c.status === 'DRAFT').length,
    rejected: allListings.filter((c) => c.status === 'REJECTED').length,
    archived: allListings.filter((c) => c.status === 'ARCHIVED').length,
  }

  const sub = subData?.data || entitlement?.subscription
  const plan = sub?.plan || entitlement?.plan
  const planName = plan?.name || 'Seller Plan'
  const subStatus = sub?.status || (entitlement?.isSubscribed ? 'ACTIVE' : 'INACTIVE')
  const isSubActive = subStatus === 'ACTIVE' || subStatus === 'TRIAL' || subStatus === 'RENEWED'

  const rawMaxCars = usage?.maxCars ?? 0
  const maxCars = rawMaxCars > 0 ? rawMaxCars : isSubActive ? 10 : 0
  const remainingCars = usage?.remainingCars ?? (isSubActive ? maxCars : 0)
  const carsUsed = Math.max(0, maxCars - remainingCars)

  const rawMaxDraft = usage?.maxDraftCars ?? 0
  const maxDraftCars = rawMaxDraft > 0 ? rawMaxDraft : isSubActive ? 5 : 0
  const remainingDraftCars = usage?.remainingDraftCars ?? (isSubActive ? maxDraftCars : 0)
  const draftCarsUsed = Math.max(0, maxDraftCars - remainingDraftCars)

  const maxImages = usage?.maxImagesPerListing && usage.maxImagesPerListing > 0 ? usage.maxImagesPerListing : 10

  const canAddCar = isSubActive && (maxCars === 0 || remainingCars > 0)

  const setTab = (tab: string) => {
    if (tab === 'ALL') {
      searchParams.delete('status')
    } else {
      searchParams.set('status', tab)
    }
    setSearchParams(searchParams)
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-display text-2xl font-bold tracking-tight text-foreground">My Cars</h1>
            <Badge variant="outline" className="border-primary/30 text-primary font-bold text-xs">
              {planName}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            Manage your pre-owned &amp; new vehicle inventory, draft listings, and moderation status.
          </p>
        </div>

        <div className="flex items-center gap-3">
          {canAddCar ? (
            <Link to="/seller/cars/new">
              <Button className="gap-2 font-bold shadow-glow">
                <Plus className="size-4" /> + Add Car
              </Button>
            </Link>
          ) : (
            <div className="flex items-center gap-2">
              <Button disabled variant="outline" className="gap-2 font-bold opacity-60">
                <Plus className="size-4" /> + Add Car
              </Button>
              <Link to="/seller/billing">
                <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs gap-1.5">
                  <Zap className="size-3.5" /> Upgrade Plan
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Subscription Guard Alert if Inactive / Quota Reached */}
      {!isSubActive && (
        <Card className="border-amber-500/40 bg-gradient-to-r from-amber-500/10 via-card to-card p-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="size-5 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-sm text-foreground">Subscription Payment Required</h3>
                <p className="text-xs text-muted-foreground">
                  Your seller subscription is currently inactive. Activate your plan to list new vehicles for sale.
                </p>
              </div>
            </div>
            <Link to="/seller/billing" className="shrink-0">
              <Button className="bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs gap-1.5">
                <Crown className="size-3.5" /> Activate Subscription
              </Button>
            </Link>
          </div>
        </Card>
      )}

      {isSubActive && maxCars > 0 && remainingCars <= 0 && (
        <Card className="border-amber-500/40 bg-gradient-to-r from-amber-500/10 via-card to-card p-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="size-5 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-sm text-foreground">Car Listing Quota Reached ({carsUsed} / {maxCars})</h3>
                <p className="text-xs text-muted-foreground">
                  You have reached the maximum allowed active vehicle listings for your {planName}. Upgrade to a higher tier for more capacity.
                </p>
              </div>
            </div>
            <Link to="/seller/billing" className="shrink-0">
              <Button className="bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs gap-1.5">
                <Zap className="size-3.5" /> Upgrade Capacity
              </Button>
            </Link>
          </div>
        </Card>
      )}

      {/* Subscription Quota Summary Card */}
      <Card className="p-5 border-border/80 bg-card/80 backdrop-blur-xl">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2 flex-1">
            <div className="flex items-center justify-between text-xs font-bold">
              <span className="text-muted-foreground flex items-center gap-1.5">
                <Car className="size-4 text-primary" /> Active Listing Quota
              </span>
              <span className="text-foreground">
                {carsUsed} / {maxCars} Used ({remainingCars} Remaining)
              </span>
            </div>
            <div className="h-2 w-full rounded-full bg-accent/60 overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${
                  carsUsed >= maxCars ? 'bg-amber-500' : 'bg-primary'
                }`}
                style={{ width: `${Math.min(100, (carsUsed / (maxCars || 1)) * 100)}%` }}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 border-t md:border-t-0 md:border-l border-border/60 pt-4 md:pt-0 md:pl-6 text-xs">
            <div>
              <span className="text-muted-foreground block text-[10px] uppercase font-semibold">Draft Quota</span>
              <span className="font-bold text-foreground">{draftCarsUsed} / {maxDraftCars} Drafts</span>
            </div>
            <div>
              <span className="text-muted-foreground block text-[10px] uppercase font-semibold">Photo Limit</span>
              <span className="font-bold text-foreground">{maxImages} Photos/Car</span>
            </div>
            <div>
              <span className="text-muted-foreground block text-[10px] uppercase font-semibold">Plan Status</span>
              <Badge variant={isSubActive ? 'success' : 'outline'} className="mt-0.5 text-[10px]">
                {subStatus}
              </Badge>
            </div>
          </div>
        </div>
      </Card>

      {/* Inventory KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Card
          onClick={() => setTab('ALL')}
          className={`p-4 space-y-1 cursor-pointer transition-all ${
            activeTab === 'ALL' ? 'border-primary ring-1 ring-primary bg-primary/5' : 'hover:bg-accent/40'
          }`}
        >
          <span className="text-[10px] text-muted-foreground uppercase font-bold flex items-center justify-between">
            Total Cars <Car className="size-3 text-muted-foreground" />
          </span>
          <div className="font-display text-2xl font-extrabold">{stats.total}</div>
        </Card>

        <Card
          onClick={() => setTab('PUBLISHED')}
          className={`p-4 space-y-1 cursor-pointer transition-all ${
            activeTab === 'PUBLISHED' ? 'border-emerald-500 ring-1 ring-emerald-500 bg-emerald-500/5' : 'hover:bg-accent/40'
          }`}
        >
          <span className="text-[10px] text-emerald-500 uppercase font-bold flex items-center justify-between">
            Published <CheckCircle2 className="size-3 text-emerald-500" />
          </span>
          <div className="font-display text-2xl font-extrabold text-emerald-500">{stats.published}</div>
        </Card>

        <Card
          onClick={() => setTab('PENDING_REVIEW')}
          className={`p-4 space-y-1 cursor-pointer transition-all ${
            activeTab === 'PENDING_REVIEW' ? 'border-amber-500 ring-1 ring-amber-500 bg-amber-500/5' : 'hover:bg-accent/40'
          }`}
        >
          <span className="text-[10px] text-amber-500 uppercase font-bold flex items-center justify-between">
            Pending <Clock className="size-3 text-amber-500" />
          </span>
          <div className="font-display text-2xl font-extrabold text-amber-500">{stats.pending}</div>
        </Card>

        <Card
          onClick={() => setTab('DRAFT')}
          className={`p-4 space-y-1 cursor-pointer transition-all ${
            activeTab === 'DRAFT' ? 'border-primary ring-1 ring-primary bg-primary/5' : 'hover:bg-accent/40'
          }`}
        >
          <span className="text-[10px] text-muted-foreground uppercase font-bold flex items-center justify-between">
            Drafts <FileEdit className="size-3 text-muted-foreground" />
          </span>
          <div className="font-display text-2xl font-extrabold">{stats.drafts}</div>
        </Card>

        <Card
          onClick={() => setTab('REJECTED')}
          className={`p-4 space-y-1 cursor-pointer transition-all ${
            activeTab === 'REJECTED' ? 'border-rose-500 ring-1 ring-rose-500 bg-rose-500/5' : 'hover:bg-accent/40'
          }`}
        >
          <span className="text-[10px] text-rose-500 uppercase font-bold flex items-center justify-between">
            Rejected <XCircle className="size-3 text-rose-500" />
          </span>
          <div className="font-display text-2xl font-extrabold text-rose-500">{stats.rejected}</div>
        </Card>

        <Card className="p-4 space-y-1 bg-accent/20">
          <span className="text-[10px] text-muted-foreground uppercase font-bold block">
            Remaining Quota
          </span>
          <div className="font-display text-2xl font-extrabold text-primary">{remainingCars}</div>
        </Card>
      </div>

      {/* Inventory Table / List */}
      {loadingListings ? (
        <div className="p-12 text-center flex flex-col items-center justify-center space-y-3 bg-card border rounded-xl">
          <Loader2 className="size-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading vehicle inventory...</p>
        </div>
      ) : allListings.length > 0 ? (
        <Card className="overflow-hidden border border-border">
          {/* Table Header Controls */}
          <div className="p-4 border-b border-border bg-accent/20 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-1.5 overflow-x-auto text-xs font-semibold">
              {['ALL', 'PUBLISHED', 'PENDING_REVIEW', 'DRAFT', 'REJECTED', 'ARCHIVED'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setTab(tab)}
                  className={`px-3 py-1.5 rounded-lg transition-all ${
                    activeTab === tab
                      ? 'bg-primary text-primary-foreground font-bold shadow-xs'
                      : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                  }`}
                >
                  {tab === 'ALL' ? 'All Cars' : tab.replace('_', ' ')}
                </button>
              ))}
            </div>
            <span className="text-xs text-muted-foreground font-medium">
              Showing {allListings.length} listing{allListings.length === 1 ? '' : 's'}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-accent/40 text-muted-foreground border-b border-border uppercase text-[10px] tracking-wider">
                <tr>
                  <th className="p-4">Vehicle</th>
                  <th className="p-4">Price &amp; Location</th>
                  <th className="p-4">Specs</th>
                  <th className="p-4">Status &amp; Moderation</th>
                  <th className="p-4">Updated</th>
                  <th className="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {allListings.map((car: any) => (
                  <tr key={car.id} className="hover:bg-accent/20 transition-colors">
                    {/* Vehicle info */}
                    <td className="p-4 font-bold max-w-xs">
                      <div className="flex items-center gap-3">
                        <div className="size-12 rounded-lg overflow-hidden border border-border shrink-0 bg-accent/20 relative">
                          {Array.isArray(car.images) && car.images[0] ? (
                            <img
                              src={typeof car.images[0] === 'string' ? car.images[0] : car.images[0].url}
                              alt={car.title}
                              className="size-full object-cover"
                            />
                          ) : (
                            <Car className="size-6 m-3 text-primary" />
                          )}
                          <span className="absolute bottom-0 right-0 bg-background/80 text-[9px] px-1 font-mono font-bold text-foreground">
                            {car.images?.length || 0}📷
                          </span>
                        </div>
                        <div className="min-w-0">
                          <div className="line-clamp-1 font-bold text-foreground">{car.title}</div>
                          <div className="text-xs font-normal text-muted-foreground">
                            {car.brand?.name || 'Brand'} • {car.model?.name || 'Model'}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Price & Location */}
                    <td className="p-4">
                      <div className="font-display font-bold text-base text-foreground">
                        ₹{Number(car.askingPrice).toLocaleString('en-IN')}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {car.locationCity}, {car.locationState}
                      </div>
                    </td>

                    {/* Specs */}
                    <td className="p-4 text-xs space-y-0.5">
                      <div className="font-semibold text-foreground">
                        {car.manufactureYear} • {car.fuelType}
                      </div>
                      <div className="text-muted-foreground">
                        {Number(car.mileageKm).toLocaleString('en-IN')} km • {car.transmission}
                      </div>
                    </td>

                    {/* Status & Moderation */}
                    <td className="p-4 max-w-xs">
                      <div className="space-y-1">
                        <Badge
                          className={
                            car.status === 'PUBLISHED'
                              ? 'bg-emerald-500/20 text-emerald-500 border border-emerald-500/30'
                              : car.status === 'PENDING_REVIEW'
                              ? 'bg-amber-500/20 text-amber-500 border border-amber-500/30'
                              : car.status === 'REJECTED'
                              ? 'bg-rose-500/20 text-rose-500 border border-rose-500/30'
                              : 'bg-muted text-muted-foreground'
                          }
                        >
                          {car.status === 'PENDING_REVIEW' ? 'PENDING REVIEW' : car.status}
                        </Badge>

                        {car.status === 'PENDING_REVIEW' && (
                          <p className="text-[10px] text-amber-500/90 font-medium">
                            Waiting for Admin moderation
                          </p>
                        )}

                        {car.status === 'REJECTED' && car.rejectionReason && (
                          <div className="p-2 rounded bg-rose-500/10 border border-rose-500/20 text-[11px] text-rose-400 font-medium space-y-0.5">
                            <span className="font-bold block text-rose-500">Rejection Reason:</span>
                            <p className="line-clamp-2">{car.rejectionReason}</p>
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Updated */}
                    <td className="p-4 text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(car.updatedAt || car.createdAt).toLocaleDateString()}
                    </td>

                    {/* Actions */}
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {car.status === 'PUBLISHED' && (
                          <Link to={appRoutes.carDetails(car.slug)}>
                            <Button size="sm" variant="ghost" className="size-8 p-0" title="View Marketplace Listing">
                              <Eye className="size-4" />
                            </Button>
                          </Link>
                        )}
                        {car.status === 'DRAFT' ? (
                          <Link to={`/seller/cars/${car.id}/edit`}>
                            <Button size="sm" variant="default" className="text-xs font-semibold gap-1">
                              <FileEdit className="size-3.5" /> Continue Editing
                            </Button>
                          </Link>
                        ) : car.status === 'REJECTED' ? (
                          <Link to={`/seller/cars/${car.id}/edit`}>
                            <Button size="sm" variant="destructive" className="text-xs font-semibold gap-1">
                              Fix &amp; Resubmit
                            </Button>
                          </Link>
                        ) : (
                          <Link to={`/seller/cars/${car.id}/edit`}>
                            <Button size="sm" variant="outline" className="size-8 p-0" title="Edit Listing">
                              <Edit className="size-4" />
                            </Button>
                          </Link>
                        )}

                        <Button
                          size="sm"
                          variant="ghost"
                          className="size-8 p-0 text-muted-foreground hover:text-rose-500"
                          onClick={() => archiveListing.mutate(car.id)}
                          title="Archive Listing"
                        >
                          <Archive className="size-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        /* Empty State */
        <Card className="p-12 text-center space-y-4 border border-dashed border-border bg-card/50">
          <PackageOpen className="size-12 mx-auto text-muted-foreground/60" />
          <div className="space-y-1">
            <h3 className="font-bold text-lg text-foreground">
              {activeTab === 'ALL'
                ? 'No cars listed yet'
                : activeTab === 'PUBLISHED'
                ? 'No published cars'
                : activeTab === 'DRAFT'
                ? 'No draft listings'
                : activeTab === 'PENDING_REVIEW'
                ? 'No pending review listings'
                : 'No listings in this category'}
            </h3>
            <p className="text-xs text-muted-foreground max-w-sm mx-auto">
              {activeTab === 'ALL'
                ? 'You have not added any vehicle listings to your seller inventory yet.'
                : `No listings with status ${activeTab} were found.`}
            </p>
          </div>
          {canAddCar && (
            <Link to="/seller/cars/new">
              <Button className="gap-2 font-bold shadow-glow">+ Add Your First Car</Button>
            </Link>
          )}
        </Card>
      )}
    </div>
  )
}
